import type { ComponentType } from 'react'
import { createHashRouter, Navigate, type RouteObject } from 'react-router-dom'
import { GuestOnlyRoute, ProtectedRoute } from '@/features/auth'

type RouteAccess = 'public' | 'protected' | 'guestOnly'

interface AppRouteConfig {
  path: string
  access: RouteAccess
  load: () => Promise<{ Component: ComponentType }>
}

// 单一来源：路由表 + 访问级别。新增页面只需在此追加一项。
const HOME_PATH = import.meta.env.VITE_HOME_PATH?.trim() ?? ''

const appRouteDefinitions: AppRouteConfig[] = [
  {
    path: '/',
    access: 'public',
    load: async () => {
      if (HOME_PATH) {
        window.location.replace(HOME_PATH)
        return { Component: () => null }
      }
      return { Component: (await import('@/pages/home')).HomePage }
    },
  },
  {
    path: '/login',
    access: 'guestOnly',
    load: async () => ({ Component: (await import('@/pages/login')).LoginPage }),
  },
  {
    path: '/flow',
    access: 'protected',
    load: async () => ({ Component: (await import('@/pages/flow-editor')).FlowEditorPage }),
  },
  {
    path: '/invite',
    access: 'public',
    load: async () => ({ Component: (await import('@/pages/invite')).InvitePage }),
  },
  {
    path: '/404',
    access: 'public',
    load: async () => ({ Component: (await import('@/pages/not-found')).NotFoundPage }),
  },
]

function wrapWithAccess(access: RouteAccess, Component: ComponentType): ComponentType {
  if (access === 'protected') {
    return () => (
      <ProtectedRoute>
        <Component />
      </ProtectedRoute>
    )
  }
  if (access === 'guestOnly') {
    return () => (
      <GuestOnlyRoute>
        <Component />
      </GuestOnlyRoute>
    )
  }
  return Component
}

function buildRoutes(): RouteObject[] {
  const routes: RouteObject[] = appRouteDefinitions.map((r) => ({
    path: r.path,
    lazy: async () => {
      const { Component } = await r.load()
      return { Component: wrapWithAccess(r.access, Component) }
    },
  }))
  routes.push({ path: '*', element: <Navigate replace to="/404" /> })
  return routes
}

export function createAppRouter() {
  return createHashRouter(buildRoutes())
}
